import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AnalyticsResourceType,
  type CustomerModel,
  DashboardModel,
  type DashboardModelSavedReportsModel,
  type ReportConfig,
  Roles,
} from "@doitintl/cmp-models";
import {
  type DocumentSnapshotModel,
  Filter,
  getCollection,
  type ModelReference,
  modelServerTimestamp,
  type WithModel,
} from "@doitintl/models-any";
import { useCollectionData, useInvalidateQueryData } from "@doitintl/models-hooks";
import { type Timestamp } from "@doitintl/models-types";
import { useMutation } from "@tanstack/react-query";
import intersection from "lodash/intersection";

import { reportText } from "../../../../assets/texts";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useIsFeatureEntitled, useTier } from "../../../../Context/TierProvider";
import { useUserContext } from "../../../../Context/UserContext";
import { type CreateReportPayload } from "../../../../Pages/CloudAnalytics/generateReport/types";
import { type ReportTemplateWithVersion } from "../../../../Pages/CloudAnalytics/templateLibrary/types";
import { getOrganizationRef } from "../../../../Pages/CloudAnalytics/utilities";
import { type ReportWSnap } from "../../../../types";
import { getCachingKeys } from "../../../../utils/cachingKeys";
import { loadDocumentsByQueriesIfPossible, TimestampFromDate } from "../../../../utils/firebase";

export const reportsTransform = (
  data: WithModel<DashboardModelSavedReportsModel>,
  ref: ModelReference<DashboardModelSavedReportsModel>
): ReportWSnap => {
  const transformedData: ReportWSnap["data"] = {
    ...data,
    customer: data.customer as ModelReference<CustomerModel>,
    owner: "",
  };

  for (const collaborator of data.collaborators ?? []) {
    if (collaborator.role === Roles.OWNER) {
      transformedData.owner = collaborator.email;
      break;
    }
  }

  return {
    data: transformedData,
    ref,
  };
};

export type ReportsValues = [ReportWSnap[], ReportWSnap[], boolean];

const emptyArr = [];

const createReport = async (payload: CreateReportPayload) => {
  const docRef = getCollection(DashboardModel).doc("google-cloud-reports").collection("savedReports").newDoc();

  const draft = payload.draft ?? true;
  const expireBy = draft ? TimestampFromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) : null;

  const reportData: DashboardModelSavedReportsModel = {
    public: null,
    type: "custom",
    name: "",
    description: "",
    timeCreated: modelServerTimestamp() as Timestamp,
    timeModified: modelServerTimestamp() as Timestamp,
    config: null,
    widgetEnabled: false,
    hidden: false,
    customer: payload.customer ?? null,
    collaborators: payload.collaborators ?? [],
    draft,
    organization: payload.organization,
    expireBy,
  };

  if (payload.config) {
    reportData.config = payload.config as ReportConfig;
  }

  if (payload.name !== "") {
    reportData.name = payload.name ?? "";
    reportData.description = payload.description ?? "";
  }

  await docRef.set(reportData);
  return docRef.id;
};

const useReportEntitlements = () => {
  const allowCustomReports = useIsFeatureEntitled("analytics:reports", true);
  const allowPresetReports = useIsFeatureEntitled("analytics:reports:presets", true);

  const isCustomReportsAllowed = !!allowCustomReports;
  const isPresetReportsAllowed = !!allowPresetReports;

  return { isCustomReportsAllowed, isPresetReportsAllowed };
};

const useReportsQueries = () => {
  const { userRoles } = useUserContext({ requiredRoles: true });
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const { userOrganization, customer } = useCustomerContext({ allowNull: true });
  const { isCustomReportsAllowed, isPresetReportsAllowed } = useReportEntitlements();
  const isSuperAdmin = userRoles.cloudAnalyticsAdmin;

  const reportsCollectionRef = useMemo(
    () => getCollection(DashboardModel).doc("google-cloud-reports").collection("savedReports"),
    []
  );

  const presetReportsQuery = useMemo(() => {
    if (!isPresetReportsAllowed) {
      return;
    }

    return reportsCollectionRef.where("type", "==", AnalyticsResourceType.PRESET).where("customer", "==", null);
  }, [isPresetReportsAllowed, reportsCollectionRef]);

  const customPublicReportsQuery = useMemo(() => {
    if (!isCustomReportsAllowed || !customer?.ref) {
      return;
    }

    let query = reportsCollectionRef
      .where("type", "==", AnalyticsResourceType.CUSTOM)
      .where("customer", "==", customer.ref)
      .where("draft", "==", false)
      .where(
        Filter.or(
          Filter.where<DashboardModelSavedReportsModel>()("public", "==", Roles.VIEWER),
          Filter.where<DashboardModelSavedReportsModel>()("public", "==", Roles.EDITOR)
        )
      );

    // Regular users (not admins or Doit employees) can only see reports from their own organization
    if (!isSuperAdmin) {
      const orgRef = getOrganizationRef(false, userOrganization, customer.id);
      const isRootOrg = orgRef.id === "root";

      if (isRootOrg) {
        // If the organization is the root organization, we also include reports that have no organization
        query = query.where(
          Filter.or(
            Filter.where<DashboardModelSavedReportsModel>()("organization", "==", orgRef),
            Filter.where<DashboardModelSavedReportsModel>()("organization", "==", null)
          )
        );
      } else {
        query = query.where("organization", "==", orgRef);
      }
    }

    return query;
  }, [reportsCollectionRef, customer?.id, customer?.ref, isCustomReportsAllowed, isSuperAdmin, userOrganization]);

  // Privately owned/shared reports
  const customSharedReportsQuery = useMemo(() => {
    if (!isCustomReportsAllowed || !customer?.ref) {
      return;
    }

    let query = reportsCollectionRef
      .where("type", "==", AnalyticsResourceType.CUSTOM)
      .where("customer", "==", customer.ref)
      .where("public", "==", null);

    if (!isSuperAdmin) {
      query = query.where("collaborators", "array-contains-any", [
        { email: currentUser.email, role: Roles.OWNER },
        { email: currentUser.email, role: Roles.VIEWER },
        { email: currentUser.email, role: Roles.EDITOR },
      ]);
    }

    return query;
  }, [reportsCollectionRef, currentUser.email, customer?.ref, isCustomReportsAllowed, isSuperAdmin]);

  return { presetReportsQuery, customPublicReportsQuery, customSharedReportsQuery };
};

export const useReportMutationWithCustomerId = (customerId: string | undefined) => {
  const { presetReportsQuery, customPublicReportsQuery, customSharedReportsQuery } = useReportsQueries();
  const invalidatePresetReports = useInvalidateQueryData(
    customerId ? presetReportsQuery : undefined,
    getCachingKeys(customerId)
  );
  const invalidateCustomPublicReports = useInvalidateQueryData(
    customerId ? customPublicReportsQuery : undefined,
    getCachingKeys(customerId)
  );
  const invalidateCustomSharedReports = useInvalidateQueryData(
    customerId ? customSharedReportsQuery : undefined,
    getCachingKeys(customerId)
  );

  return useMutation({
    mutationFn: (payload: CreateReportPayload) => createReport(payload),
    onSuccess: () => {
      // TODO only invalidate on non realtime platforms
      invalidatePresetReports?.();
      invalidateCustomPublicReports?.();
      invalidateCustomSharedReports?.();
    },
  });
};

export const useReportsData = () => {
  const { customer } = useCustomerContext();
  const { presetReportsQuery, customPublicReportsQuery, customSharedReportsQuery } = useReportsQueries();
  const { isCustomReportsAllowed, isPresetReportsAllowed } = useReportEntitlements();

  const reportsTransformWithEmail = useCallback(
    (
      data: WithModel<DashboardModelSavedReportsModel>,
      docSnapshot: DocumentSnapshotModel<DashboardModelSavedReportsModel>
    ) => reportsTransform(data, docSnapshot.modelRef),
    []
  );

  const [presetReports, loadingPresetReports, presetReportsLoadingError] = useCollectionData(presetReportsQuery, {
    transform: reportsTransformWithEmail,
    caching: true,
    cachingKeys: getCachingKeys(customer.id),
  });

  const [customPublicReports, loadingCustomPublicReports, customPublicReportsLoadingError] = useCollectionData(
    customPublicReportsQuery,
    {
      transform: reportsTransformWithEmail,
      caching: true,
      cachingKeys: getCachingKeys(customer.id),
    }
  );

  const [customSharedReports, loadingCustomSharedReports, customSharedReportsLoadingError] = useCollectionData(
    customSharedReportsQuery,
    {
      transform: reportsTransformWithEmail,
      caching: true,
      cachingKeys: getCachingKeys(customer.id),
    }
  );

  return {
    preset: {
      reports: presetReports,
      loading: loadingPresetReports && isPresetReportsAllowed,
      error: presetReportsLoadingError,
    },
    customPublic: {
      reports: customPublicReports,
      loading: loadingCustomPublicReports && isCustomReportsAllowed,
      error: customPublicReportsLoadingError,
    },
    customShared: {
      reports: customSharedReports,
      loading: loadingCustomSharedReports && isCustomReportsAllowed,
      error: customSharedReportsLoadingError,
    },
  };
};

// Do not use this hook directly!
export const useReports = (
  reportTemplatesWithVersions: ReportTemplateWithVersion[] | null,
  managedReportsIds: string[] | null,
  filteredReportTemplateFavorites: string[]
): ReportsValues => {
  const { customer, customerOrPresentationModeCustomer } = useCustomerContext();
  const [managedReports, setManagedReports] = useState<ReportWSnap[] | null>(null);

  const { getFeatureKey } = useTier();
  const allowReportTemplate = useIsFeatureEntitled("analytics:reports:templates", true);
  const allowFlexsaveAWS = useIsFeatureEntitled("savings:flexsaveAWS");
  const allowPdiEks = useIsFeatureEntitled("pdi:eks");

  const data = useReportsData();

  const fetchManagedReports = useCallback(async () => {
    if (!managedReportsIds || !customer.ref) {
      return;
    }

    const reports = await loadDocumentsByQueriesIfPossible(
      managedReportsIds.map((id) =>
        getCollection(DashboardModel).doc("google-cloud-reports").collection("savedReports").doc(id)
      ),
      {
        useQueryToRetrieveDocs: true,
        useDataField: true,
        refField: "ref",
      }
    );

    const reportsNotUndefined = reports
      .filter((r) => !!r)
      .map((r) => ({
        data: {
          ...r.data,
          customer: customer.ref,
          owner: "",
        },
        filters: undefined,
        ref: r.ref,
      }));

    setManagedReports(reportsNotUndefined);
  }, [customer.ref, managedReportsIds]);

  useEffect(() => {
    if (allowReportTemplate) {
      fetchManagedReports();
    }
  }, [fetchManagedReports, allowReportTemplate]);

  // when feature is not allowed in tier (query = undefined), loading will always be true
  // in this case set loading to false explicitly
  const reportsLoading = data.preset.loading || data.customPublic.loading || data.customShared.loading;

  const favoriteReportTemplatesActiveReports = useMemo(
    () =>
      filteredReportTemplateFavorites.length
        ? (reportTemplatesWithVersions ?? [])
            .filter((templateWithVersion) => filteredReportTemplateFavorites.includes(templateWithVersion.template.id))
            .map((templateWithVersion) => templateWithVersion.template.activeReport?.id)
            .filter(Boolean)
        : [],
    [filteredReportTemplateFavorites, reportTemplatesWithVersions]
  );

  const reportsList = useMemo(() => {
    if (reportsLoading) {
      return emptyArr;
    }

    const filteredPresetReports = data.preset.reports?.filter((report) => {
      const entitlementsKeys = report.data.entitlements?.map((entitlement) => getFeatureKey(entitlement)) ?? [];
      const isEntitlementReport = report.data.entitlements && report.data.entitlements.length > 0;
      const isFlexsaveReport = allowFlexsaveAWS && entitlementsKeys.includes("savings:flexsaveAWS");
      const isEKSReport = allowPdiEks && entitlementsKeys.includes("pdi:eks");
      return !isEntitlementReport || isFlexsaveReport || isEKSReport;
    });

    return [
      ...(filteredPresetReports ?? []),
      ...(managedReports ?? []),
      ...(data.customPublic.reports ?? []),
      ...(data.customShared.reports ?? []),
    ];
  }, [
    allowPdiEks,
    data.customPublic.reports,
    data.customShared.reports,
    data.preset.reports,
    managedReports,
    reportsLoading,
    allowFlexsaveAWS,
    getFeatureKey,
  ]);

  const filteredReports = useMemo(() => {
    if (reportsLoading) {
      return emptyArr;
    }

    return reportsList.filter((report) => {
      let cloudFilter = true;

      if (report.data.type === AnalyticsResourceType.PRESET && report.data.cloud?.length) {
        cloudFilter = intersection(customerOrPresentationModeCustomer?.assets, report.data.cloud).length > 0;
      }

      if (report.data.type === AnalyticsResourceType.MANAGED) {
        cloudFilter = favoriteReportTemplatesActiveReports.includes(report.ref.id);
      }

      return (
        cloudFilter && !report.data.draft && !report.data.hidden && report.data.name !== reportText.EXPORTED_REPORT
      );
    });
  }, [reportsLoading, reportsList, customerOrPresentationModeCustomer?.assets, favoriteReportTemplatesActiveReports]);

  return [reportsList, filteredReports, reportsLoading];
};
